import React, { useMemo } from 'react';
import { themeColours } from 'fym-shared/src/music24/releaseThemes';
import { PROFILE_BANNER_KINDS, type ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { useArtistProfile } from '../../studio/spotifyHeader/useArtistProfile';
import { StudioActionError } from '../../studio/StudioActionError';
import { profileIsEmpty } from '../profileSlots';
import type { ProfileBannerKind } from '../profileRoute';
import type { ProfileRoute } from '../useProfileRoute';
import { useProfileMake } from '../useProfileMake';
import { ProfileFreeBanner } from './ProfileFreeBanner';
import { ProfileLookStrip } from './ProfileLookStrip';
import { ProfileEmptyState } from './ProfileEmptyState';
import { ProfileSpotifyCard } from './ProfileSpotifyCard';
import { ProfileBannerCard } from './ProfileBannerCard';
import { ProfilePressCard } from './ProfilePressCard';
import { ProfileBioCard } from './ProfileBioCard';
import { ProfilePhoneRows } from './ProfilePhoneRows';
import { useProfileRetry } from './useProfileRetry';
import type { ProfileCardProps } from './profileCardProps';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfilePageProps {
  accountId: number;
  profile: ArtistProfileResponse;
  route: ProfileRoute;
}

/**
 * The Profile page (design a1 paid, a2 Free first visit, a3 states, d below 700 px): the Free
 * banner, the artist look, the empty state, then one card per kind. Below 700 px the Spotify card
 * comes first and every other kind is one row that opens its tool.
 */
const ProfilePageComponent: React.FC<ProfilePageProps> = ({ accountId, profile, route }) => {
  const narrow = useIsNarrowScreen();
  const artist = useArtistProfile(accountId);
  const make = useProfileMake(accountId);
  const retry = useProfileRetry(make, route.openTool);
  const colours = useMemo(() => themeColours(profile.look.themeId, profile.look.palette), [profile.look]);
  const avatarUrl = profile.avatar === null ? artist.avatarUrl : getFullAPIFileURL(profile.avatar.url);

  const artistName = profile.spotify.artistName ?? artist.name ?? '';
  const { openTool, openSheet, openBioSheet } = route;
  const isMaking = make.isBusy;
  const cardProps = useMemo<ProfileCardProps>(
    () => ({
      accountId,
      profile,
      colours,
      artistName,
      avatarUrl,
      narrow,
      openTool,
      openSheet,
      openBioSheet,
      retry,
      isMaking,
    }),
    [accountId, artistName, avatarUrl, colours, isMaking, narrow, openBioSheet, openSheet, openTool, profile, retry]
  );

  return (
    <div className={styles.page} data-testid="profile-page">
      {profile.preview && <ProfileFreeBanner />}
      <ProfileLookStrip profile={profile} colours={colours} />
      {profileIsEmpty(profile.assets) && <ProfileEmptyState accountId={accountId} profile={profile} />}
      <StudioActionError feedback={make.feedback} />
      {narrow ? (
        <>
          <ProfileSpotifyCard card={cardProps} />
          <ProfilePhoneRows card={cardProps} />
        </>
      ) : (
        <div className={styles.grid}>
          <ProfileSpotifyCard card={cardProps} />
          {(PROFILE_BANNER_KINDS as readonly ProfileBannerKind[]).map((kind) => (
            <ProfileBannerCard key={kind} card={cardProps} kind={kind} />
          ))}
          <ProfilePressCard card={cardProps} />
          <ProfileBioCard card={cardProps} />
        </div>
      )}
    </div>
  );
};

export const ProfilePage = TypedMemo(ProfilePageComponent);
